import { Controller } from "@hotwired/stimulus";

import { DESKTOP_MIN_WIDTH, prefersReducedMotion } from "~/lib/viewport";
import { clamp01 } from "~/lib/web/ease";
import { registerScrub } from "~/lib/web/scrub";

export default class WebStepsController extends Controller<HTMLElement> {
  static override targets = ["step", "dot"];
  declare readonly stepTargets: HTMLElement[];
  declare readonly dotTargets: HTMLElement[];

  // The markup ships with the first step live, so there is nothing to write
  // until the section has been scrolled into.
  private index = 0;
  private unregister: (() => void) | null = null;
  // registerScrub calls scrub() in the write phase, where reading
  // window.innerWidth would flush layout. A media query list is answered
  // from the engine's own state and holds no listener of its own.
  private readonly desktop = window.matchMedia(
    `(min-width: ${DESKTOP_MIN_WIDTH}px)`,
  );

  override connect(): void {
    // Pinning the section and cutting between steps as the reader scrolls is
    // itself the motion here, so reduced motion keeps the list in the markup.
    if (prefersReducedMotion()) return;
    this.element.classList.add("is-active");
    this.unregister = registerScrub(this.element, (rect, viewportHeight) =>
      this.scrub(rect, viewportHeight),
    );
  }

  override disconnect(): void {
    this.unregister?.();
    this.unregister = null;
    this.element.classList.remove("is-active");
  }

  private scrub(rect: DOMRect, viewportHeight: number): void {
    // The viewport can cross back under the threshold, where how.css lays the
    // section out as a list again and the step on screen is all of them.
    if (!this.desktop.matches) return;

    // The section is one viewport taller than the distance it is held for, so
    // this is how far through the hold the reader is.
    const held = Math.max(1, rect.height - viewportHeight);
    const progress = clamp01(-rect.top / held);
    // Rounding is what makes the step a cut at the halfway point of its own
    // screen of scroll, rather than a value the copy is interpolated over.
    const index = Math.round(progress * (this.stepTargets.length - 1));
    if (index === this.index) return;

    this.index = index;
    for (const targets of [this.stepTargets, this.dotTargets]) {
      targets.forEach((target, i) => {
        target.classList.toggle("is-live", i === index);
      });
    }
  }
}
